import * as React from 'react';
import clsx from 'clsx';
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react';
import { ChevronLeft, ChevronRight } from 'lucide-react-0.367';

import { Button, type ButtonProps as LocalButtonProps } from '../Button';

import styles from './styles/Carousel.module.scss';

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

export type CarouselProps = {
  controlsPosition?: 'inside' | 'outside';
  /** Horizontal only: fades the left/right edge while there is more to scroll that way. */
  edgeFade?: boolean;
  /** Unmount a control while it cannot scroll, instead of disabling it. */
  hideControlsAtEnds?: boolean;
  opts?: CarouselOptions;
  orientation?: 'horizontal' | 'vertical';
  plugins?: CarouselPlugin;
  setApi?: (api: CarouselApi) => void;
};

type CarouselContextProps = {
  api: ReturnType<typeof useEmblaCarousel>[1];
  canScrollNext: boolean;
  canScrollPrev: boolean;
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  scrollNext: () => void;
  scrollPrev: () => void;
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

function useCarousel() {
  const context = React.useContext(CarouselContext);

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />');
  }

  return context;
}

const Carousel = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & CarouselProps>(
  (
    {
      children,
      className,
      controlsPosition = 'inside',
      edgeFade = false,
      hideControlsAtEnds = false,
      opts,
      orientation = 'horizontal',
      plugins,
      setApi,
      ...props
    },
    ref,
  ) => {
    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === 'horizontal' ? 'x' : 'y',
      },
      plugins,
    );
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);

    // Embla's canScroll* follow the snap index, but a dragFree glide can rest between snaps.
    const tracksOffset = Boolean(opts?.dragFree) && !opts?.loop;

    const onSelect = React.useCallback(
      (api: CarouselApi) => {
        if (!api) {
          return;
        }

        if (tracksOffset) {
          const overflows = api.scrollSnapList().length > 1;
          const progress = api.scrollProgress();

          setCanScrollPrev(overflows && progress > 0.001);
          setCanScrollNext(overflows && progress < 0.999);
          return;
        }

        setCanScrollPrev(api.canScrollPrev());
        setCanScrollNext(api.canScrollNext());
      },
      [tracksOffset],
    );

    const scrollPrev = React.useCallback(() => {
      api?.scrollPrev();
    }, [api]);

    const scrollNext = React.useCallback(() => {
      api?.scrollNext();
    }, [api]);

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          scrollPrev();
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          scrollNext();
        }
      },
      [scrollPrev, scrollNext],
    );

    React.useEffect(() => {
      if (!api || !setApi) {
        return;
      }

      setApi(api);
    }, [api, setApi]);

    React.useEffect(() => {
      if (!api) {
        return;
      }

      onSelect(api);
      api.on('reInit', onSelect);
      api.on('select', onSelect);
      if (tracksOffset) {
        api.on('scroll', onSelect);
      }

      return () => {
        api?.off('reInit', onSelect);
        api?.off('select', onSelect);
        api?.off('scroll', onSelect);
      };
    }, [api, onSelect, tracksOffset]);

    const contextValue = React.useMemo(
      () => ({
        carouselRef,
        api: api,
        opts,
        orientation: orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
        hideControlsAtEnds,
      }),
      [carouselRef, api, opts, orientation, scrollPrev, scrollNext, canScrollPrev, canScrollNext, hideControlsAtEnds],
    );

    return (
      <CarouselContext.Provider value={contextValue}>
        <div
          aria-roledescription="carousel"
          className={clsx(
            styles.carousel,
            controlsPosition === 'outside' && styles.controlsOutside,
            edgeFade && orientation === 'horizontal' && styles.edgeFade,
            className,
          )}
          data-can-scroll-next={canScrollNext}
          data-can-scroll-prev={canScrollPrev}
          data-cxy-name="carousel"
          onKeyDownCapture={handleKeyDown}
          ref={ref}
          role="region"
          {...props}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    );
  },
);
Carousel.displayName = 'Carousel';

const CarouselContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const { carouselRef, orientation } = useCarousel();

    return (
      <div className={styles.carouselViewport} ref={carouselRef}>
        <div
          className={clsx(
            styles.carouselContainer,
            orientation === 'horizontal' ? styles.carouselContainerHorizontal : styles.carouselContainerVertical,
            className,
          )}
          ref={ref}
          {...props}
        />
      </div>
    );
  },
);
CarouselContent.displayName = 'CarouselContent';

const CarouselItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const { orientation } = useCarousel();

    return (
      <div
        aria-roledescription="slide"
        className={clsx(
          styles.carouselItem,
          orientation === 'horizontal' ? styles.carouselItemHorizontal : styles.carouselItemVertical,
          className,
        )}
        ref={ref}
        role="group"
        {...props}
      />
    );
  },
);
CarouselItem.displayName = 'CarouselItem';

const CarouselPrevious = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof Button>>(
  ({ className, size = 'icon', variant = 'outline', ...props }, ref) => {
    const { canScrollPrev, hideControlsAtEnds, orientation, scrollPrev } = useCarousel();

    if (hideControlsAtEnds && !canScrollPrev) {
      return null;
    }

    return (
      <Button
        className={clsx(
          styles.carouselControl,
          styles.carouselPreviousButton,
          orientation === 'horizontal'
            ? styles.carouselPreviousButtonHorizontal
            : styles.carouselPreviousButtonVertical,
          className,
        )}
        disabled={!canScrollPrev}
        onClick={scrollPrev}
        ref={ref}
        size={size as LocalButtonProps['size']}
        variant={variant as LocalButtonProps['variant']}
        {...props}
      >
        <ChevronLeft className={styles.carouselControlIcon} />
        <span className={styles.srOnly}>Previous slide</span>
      </Button>
    );
  },
);
CarouselPrevious.displayName = 'CarouselPrevious';

const CarouselNext = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof Button>>(
  ({ className, size = 'icon', variant = 'outline', ...props }, ref) => {
    const { canScrollNext, hideControlsAtEnds, orientation, scrollNext } = useCarousel();

    if (hideControlsAtEnds && !canScrollNext) {
      return null;
    }

    return (
      <Button
        className={clsx(
          styles.carouselControl,
          styles.carouselNextButton,
          orientation === 'horizontal' ? styles.carouselNextButtonHorizontal : styles.carouselNextButtonVertical,
          className,
        )}
        disabled={!canScrollNext}
        onClick={scrollNext}
        ref={ref}
        size={size as LocalButtonProps['size']}
        variant={variant as LocalButtonProps['variant']}
        {...props}
      >
        <ChevronRight className={styles.carouselControlIcon} />
        <span className={styles.srOnly}>Next slide</span>
      </Button>
    );
  },
);
CarouselNext.displayName = 'CarouselNext';

export { Carousel, type CarouselApi, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious };
